/**
 * AML / ID-V tab on the Case File.
 *
 * Surfaces the full history of AML checks per client — verdict, when
 * the email was sent, raw provider response, full event timeline. Built
 * for two audiences:
 *
 *   1. **Solicitors** — quickly see who's been chased, who's clear, who
 *      needs follow-up; one-click resend / refresh / re-run.
 *   2. **SRA inspectors / partners** — single audit-trail surface
 *      months later when the question is "show me the AML evidence on
 *      the Smith conveyance from August 2024".
 */
import DiditVerifyCard from "@/components/app/DiditVerifyCard";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/card";
import {
  ShieldCheck,
  ShieldWarning,
  ShieldSlash,
  PaperPlaneTilt,
  ArrowClockwise,
  Plus,
  Clock,
  Link as LinkIcon,
  Envelope,
  Warning,
  CheckCircle,
  Paperclip,
  FileArrowDown,
  FolderSimple,
  CaretDown,
} from "@phosphor-icons/react";

const BUCKET_META = {
  clear: {
    label: "Clear",
    chipCls: "bg-emerald-100 text-emerald-700",
    iconCls: "text-emerald-600",
    Icon: ShieldCheck,
  },
  warn: {
    label: "Refer / Consider",
    chipCls: "bg-amber-100 text-amber-800",
    iconCls: "text-amber-600",
    Icon: ShieldWarning,
  },
  fail: {
    label: "Failed",
    chipCls: "bg-rose-100 text-rose-700",
    iconCls: "text-rose-600",
    Icon: ShieldSlash,
  },
  open: {
    label: "Pending",
    chipCls: "bg-blue-100 text-blue-700",
    iconCls: "text-blue-600",
    Icon: ShieldCheck,
  },
};

function VerdictPill({ check }) {
  const bucket = classifyVerdict(check.verdict || check.status);
  const meta = BUCKET_META[bucket] || BUCKET_META.open;
  return (
    <Badge className={`${meta.chipCls} border-0 hover:${meta.chipCls.split(" ")[0]}`} data-testid={`aml-verdict-${check.id}`}>
      <meta.Icon weight="fill" className="w-3 h-3 mr-1" />
      {meta.label}
    </Badge>
  );
}

function classifyVerdict(v) {
  const s = (v || "").toLowerCase();
  if (["clear", "completed", "approved", "pass"].includes(s)) return "clear";
  if (["consider", "refer", "requisition"].includes(s)) return "warn";
  if (["fail", "failed", "rejected"].includes(s)) return "fail";
  return "open";
}

function WatchlistPanel({ data, checkId }) {
  const [open, setOpen] = useState(false);
  const summary = data?.summary || { pep: 0, sanctions: 0, rca: 0, sip: 0, total: 0 };
  const matches = data?.matches || [];
  const hasHits = (summary.total || 0) > 0;
  return (
    <div
      className={`mt-2 rounded border ${hasHits ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50"}`}
      data-testid={`watchlist-panel-${checkId}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between px-2 py-1.5 text-[11px] font-semibold ${hasHits ? "text-rose-800" : "text-emerald-800"}`}
        data-testid={`watchlist-toggle-${checkId}`}
      >
        <span className="flex items-center gap-1.5">
          {hasHits ? "⚠" : "✓"}
          Watchlist screening
          {hasHits && <span className="font-bold">— {summary.total} match{summary.total === 1 ? "" : "es"}</span>}
        </span>
        <span className="text-[10px] font-normal opacity-70">
          {summary.pep > 0 && `PEP: ${summary.pep}`}
          {summary.sanctions > 0 && ` · SAN: ${summary.sanctions}`}
          {summary.rca > 0 && ` · RCA: ${summary.rca}`}
          {summary.sip > 0 && ` · SIP: ${summary.sip}`}
          {!hasHits && "Clear — PEP/SAN/RCA/SIP"}
        </span>
      </button>
      {open && hasHits && matches.length > 0 && (
        <ul className="border-t border-rose-100 bg-white divide-y divide-slate-100 max-h-64 overflow-auto">
          {matches.map((m) => (
            <li key={m.id} className="px-3 py-2 text-[11px]" data-testid={`watchlist-match-${m.id}`}>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-semibold text-slate-900">{m.name || "Unknown"}</span>
                <span className="uppercase text-[9px] font-bold px-1 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200">
                  {m.list_type || "MATCH"}
                </span>
                {m.confidence != null && (
                  <span className="text-[9px] text-slate-500">confidence {m.confidence}</span>
                )}
              </div>
              <div className="text-slate-600">
                {m.list_name && <>List: {m.list_name}</>}
                {m.position && <> · {m.position}</>}
                {m.country && <> · {m.country}</>}
                {m.date_of_birth && <> · DOB {m.date_of_birth}</>}
              </div>
              {m.notes && <p className="text-slate-500 mt-0.5">{m.notes}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function ClientChecksCard({ caseId, client, checks, onUpdate, onOrderNew }) {
  const [refreshing, setRefreshing] = useState(null);
  const [resending, setResending] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [docsByCheck, setDocsByCheck] = useState({});
  // Watchlist (PEP / Sanctions / RCA / SIP) results — SmartSearch runs
  // these automatically on every subject, we just have to fetch them.
  const [watchlistByCheck, setWatchlistByCheck] = useState({});
  const [watchlistLoading, setWatchlistLoading] = useState(null);

  // Fetch attached documents + watchlist screening for each check once
  useEffect(() => {
    checks.forEach((check) => {
      if (docsByCheck[check.id] === undefined) {
        API.get(`/aml/${check.id}/documents`)
          .then(({ data }) => setDocsByCheck((m) => ({ ...m, [check.id]: data.documents || [] })))
          .catch(() => {});
      }
      // Only pull watchlist for SmartSearch checks that have a subject
      // ID (the ID we need to query against).
      if (
        watchlistByCheck[check.id] === undefined
        && check.provider === "smartsearch"
        && check.provider_subject_id
      ) {
        API.get(`/aml/${check.id}/watchlist`)
          .then(({ data }) => setWatchlistByCheck((m) => ({ ...m, [check.id]: data })))
          .catch(() => {
            // 400 typically means "still initialising" — leave the
            // slot empty so the UI shows nothing rather than an error.
            setWatchlistByCheck((m) => ({ ...m, [check.id]: null }));
          });
      }
    });
     
  }, [checks]);

  const latest = checks[0]; // checks are sorted desc by created_at
  const bucket = latest ? classifyVerdict(latest.verdict || latest.status) : "none";
  const Icon = (BUCKET_META[bucket] || {}).Icon || ShieldCheck;
  const iconCls = (BUCKET_META[bucket] || {}).iconCls || "text-slate-400";

  const handleRefresh = async (check) => {
    setRefreshing(check.id);
    const tid = toast.loading("Re-polling Thirdfort…");
    try {
      const { data } = await API.post(`/aml/${check.id}/refresh`);
      onUpdate(data);
      toast.success(`Status: ${data.status}${data.verdict ? ` · ${data.verdict}` : ""}`, { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refresh failed", { id: tid });
    } finally {
      setRefreshing(null);
    }
  };

  const handleResend = async (check) => {
    setResending(check.id);
    const tid = toast.loading(`Re-sending verification link to ${check.client_email}…`);
    try {
      await API.post(`/aml/${check.id}/resend-email`);
      // Add an event locally for immediate feedback
      onUpdate({
        ...check,
        events: [
          ...(check.events || []),
          { ts: new Date().toISOString(), type: "email_resent", message: "Verification link re-sent." },
        ],
      });
      toast.success("Verification link re-sent.", { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resend failed", { id: tid });
    } finally {
      setResending(null);
    }
  };

  const handleUpload = async (check, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(check.id);
    const tid = toast.loading(`Uploading ${file.name}…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post(`/aml/${check.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocsByCheck((m) => ({ ...m, [check.id]: [data, ...(m[check.id] || [])] }));
      toast.success(`Attached ${file.name}`, { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed", { id: tid });
    } finally {
      setUploading(null);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const resp = await API.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: doc.content_type || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  return (
    <Card className="border-slate-200" data-testid={`aml-card-${client.key}`}>
      <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-slate-50 ${iconCls}`}>
            <Icon weight="duotone" className="w-5 h-5" />
          </div>
          <div>
            <CardTitle className="text-base text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              {client.name}
            </CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">
              {client.email}{client.case_role ? ` · ${client.case_role}` : ""}
            </p>
            {!client.id && (
              <p className="text-[11px] text-slate-400 mt-0.5 italic">Portal invite pending</p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {latest ? <VerdictPill check={latest} /> : (
            <Badge className="bg-slate-100 text-slate-500 border-0">Not ordered</Badge>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="text-xs h-7"
                data-testid={`aml-order-${client.key}`}
              >
                <Plus weight="bold" className="w-3 h-3 mr-1" />
                {latest ? "Re-run check" : "Order check"}
                <CaretDown weight="bold" className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid={`aml-order-menu-${client.key}`}>
              <DropdownMenuLabel className="text-[10px] uppercase text-slate-500">
                Type of verification
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => onOrderNew(client, "aml")}
                data-testid={`aml-order-aml-${client.key}`}
              >
                <div>
                  <p className="text-sm font-medium">AML check</p>
                  <p className="text-[10px] text-slate-500">CRA + PEP + Sanctions screening</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onOrderNew(client, "biometric")}
                disabled={!latest || latest.provider !== "smartsearch"}
                data-testid={`aml-order-biometric-${client.key}`}
              >
                <div>
                  <p className="text-sm font-medium">Biometric ID-V</p>
                  <p className="text-[10px] text-slate-500">
                    {!latest || latest.provider !== "smartsearch"
                      ? "Requires prior SmartSearch AML check"
                      : "Document + selfie liveness (SmartDoc)"}
                  </p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onOrderNew(client, "both")}
                data-testid={`aml-order-both-${client.key}`}
              >
                <div>
                  <p className="text-sm font-medium">AML + Biometric</p>
                  <p className="text-[10px] text-slate-500">
                    Run both — highest assurance
                  </p>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      {checks.length === 0 ? (
        <CardContent>
          <div className="text-center py-6 text-xs text-slate-400 italic" data-testid={`aml-empty-${client.key}`}>
            No AML / ID-V checks on file for this client.
          </div>
        </CardContent>
      ) : (
        <CardContent className="space-y-3">
          {checks.map((check, idx) => (
            <div
              key={check.id}
              className={`border rounded-lg p-3 ${idx === 0 ? "border-slate-300 bg-white" : "border-slate-200 bg-slate-50"}`}
              data-testid={`aml-check-${check.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <VerdictPill check={check} />
                    <span className="text-[11px] text-slate-500">
                      {check.provider === "thirdfort" ? "Thirdfort" : check.provider}
                    </span>
                    {(check.check_type === "biometric" || check.biometric) && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10px] font-semibold uppercase"
                        title="Biometric ID-V (SmartSearch SmartDoc) has been initiated for this client."
                        data-testid={`aml-biometric-chip-${check.id}`}
                      >
                        Biometric
                      </span>
                    )}
                    {check.check_type === "both" && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-[10px] font-semibold uppercase"
                        title="AML + Biometric ID-V — highest assurance."
                      >
                        AML + Bio
                      </span>
                    )}
                    {idx === 0 && (
                      <span className="text-[10px] uppercase tracking-wide font-semibold text-[#1462a0]">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Clock weight="bold" className="w-3 h-3" />
                    Ordered {new Date(check.created_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
                    {check.ordered_by_name ? ` by ${check.ordered_by_name}` : ""}
                  </p>
                  {check.provider_check_id && (
                    <p className="text-[11px] text-slate-400 font-mono mt-0.5 truncate">
                      Ref: {check.provider_check_id}
                    </p>
                  )}
                  {check.biometric?.verification_url && (
                    <p className="text-[11px] text-indigo-600 mt-1 flex items-center gap-1" data-testid={`aml-biometric-link-${check.id}`}>
                      <a href={check.biometric.verification_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        Biometric link →
                      </a>
                      {check.biometric.expires_at && (
                        <span className="text-slate-400 text-[10px]">
                          expires {new Date(check.biometric.expires_at).toLocaleString("en-GB", { dateStyle: "short" })}
                        </span>
                      )}
                    </p>
                  )}
                  {check.biometric?.status === "rejected" && (
                    <p className="text-[11px] text-amber-700 mt-1 italic" data-testid={`aml-biometric-error-${check.id}`}>
                      Biometric ID-V: {check.biometric.message}
                    </p>
                  )}
                  {/* Watchlist screening — PEP / Sanctions / RCA / SIP.
                      Runs automatically for every SmartSearch subject
                      so we always show it if the data is available. */}
                  {watchlistByCheck[check.id] && (
                    <WatchlistPanel data={watchlistByCheck[check.id]} checkId={check.id} />
                  )}
                </div>
                <div className="flex flex-col gap-1.5 items-end shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs h-7"
                    onClick={() => handleRefresh(check)}
                    disabled={refreshing === check.id}
                    data-testid={`aml-refresh-${check.id}`}
                  >
                    <ArrowClockwise weight="bold" className={`w-3 h-3 mr-1 ${refreshing === check.id ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                  {check.verification_url && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs h-7"
                      onClick={() => handleResend(check)}
                      disabled={resending === check.id}
                      data-testid={`aml-resend-${check.id}`}
                    >
                      <PaperPlaneTilt weight="bold" className="w-3 h-3 mr-1" />
                      Resend
                    </Button>
                  )}
                  {check.provider === "smartsearch" && check.provider_subject_id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs h-7"
                      onClick={async () => {
                        const tid = toast.loading("Fetching report…");
                        try {
                          const { data } = await API.get(`/aml/${check.id}/report.pdf`, {
                            responseType: "blob",
                          });
                          const url = URL.createObjectURL(data);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `smartsearch-${check.provider_subject_id}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          URL.revokeObjectURL(url);
                          // The backend auto-files a copy into
                          // Documents / AML on first download —
                          // reflect that in the toast so the solicitor
                          // knows they don't need to also drag it in.
                          toast.success(
                            check.pdf_saved_to_documents
                              ? "Report downloaded"
                              : "Report downloaded — also saved to Documents / AML",
                            { id: tid },
                          );
                          // Refresh parent state so the "In Documents"
                          // chip appears without a full-page reload.
                          if (onUpdate) onUpdate();
                        } catch (err) {
                          const detail =
                            (err?.response?.data && typeof err.response.data === "string" && err.response.data) ||
                            err?.response?.data?.detail ||
                            "Download failed";
                          toast.error(detail, { id: tid });
                        }
                      }}
                      data-testid={`aml-download-report-${check.id}`}
                    >
                      <FileArrowDown weight="bold" className="w-3 h-3 mr-1" />
                      Report PDF
                    </Button>
                  )}
                  {check.pdf_saved_to_documents && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full px-2 py-0.5 font-semibold"
                      title="A copy of this SmartSearch report has been auto-filed into Documents / AML for SRA audit trail."
                      data-testid={`aml-saved-chip-${check.id}`}
                    >
                      <FolderSimple weight="fill" className="w-2.5 h-2.5" />
                      In Documents
                    </span>
                  )}
                </div>
              </div>

              {check.verification_url && (
                <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5 mt-2 flex items-center gap-2 text-xs">
                  <LinkIcon weight="bold" className="w-3 h-3 text-slate-400 shrink-0" />
                  <a
                    href={check.verification_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1462a0] hover:underline truncate flex-1"
                  >
                    {check.verification_url}
                  </a>
                </div>
              )}

              {/* Attached documents — Thirdfort PDF reports, scanned proof of ID, etc. */}
              <div className="mt-2 border-t border-slate-100 pt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] uppercase tracking-wide text-slate-500">
                    Documents {(docsByCheck[check.id] || []).length > 0 && (
                      <span className="text-slate-700 font-bold">({(docsByCheck[check.id] || []).length})</span>
                    )}
                  </span>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      onChange={(e) => handleUpload(check, e)}
                      disabled={uploading === check.id}
                      className="hidden"
                      data-testid={`aml-upload-${check.id}`}
                    />
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-[#1462a0] hover:bg-[#1462a0]/10 rounded transition-colors">
                      <Paperclip weight="bold" className="w-3 h-3" />
                      {uploading === check.id ? "Uploading…" : "Attach"}
                    </span>
                  </label>
                </div>
                {(docsByCheck[check.id] || []).length === 0 ? (
                  <p className="text-[11px] text-slate-400 italic">
                    Attach the Thirdfort verification report PDF or any other ID-V evidence here.
                  </p>
                ) : (
                  <div className="space-y-1">
                    {(docsByCheck[check.id] || []).map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => handleDownload(d)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded bg-white border border-slate-200 hover:border-[#1462a0] transition-colors text-xs text-left"
                        data-testid={`aml-doc-${d.id}`}
                      >
                        <Paperclip weight="bold" className="w-3 h-3 text-slate-400 shrink-0" />
                        <span className="flex-1 text-slate-900 font-medium truncate">{d.original_filename}</span>
                        <span className="text-[10px] text-slate-400 shrink-0">{new Date(d.created_at).toLocaleDateString("en-GB")}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {(check.events || []).length > 0 && (
                <details className="mt-2" data-testid={`aml-events-${check.id}`}>
                  <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-700">
                    Activity log ({check.events.length} event{check.events.length === 1 ? "" : "s"})
                  </summary>
                  <div className="mt-2 space-y-1 pl-2 border-l-2 border-slate-200">
                    {[...check.events].reverse().map((ev, i) => (
                      <div key={i} className="text-[11px] text-slate-600 flex items-start gap-2">
                        <span className="text-slate-400 shrink-0 font-mono">
                          {new Date(ev.ts).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
                        </span>
                        <span>{ev.message}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
}

export default function AmlTab({ caseId, caseData, onCaseChange }) {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState({ checks: [], by_client: {}, case_summary: {} });
  const [providers, setProviders] = useState({ providers: [], default: "thirdfort" });
  const [savingProvider, setSavingProvider] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [hist, provs] = await Promise.all([
        API.get(`/cases/${caseId}/aml-checks`),
        API.get(`/aml/providers`).catch(() => ({ data: { providers: [], default: "thirdfort" } })),
      ]);
      setHistory(hist.data);
      setProviders(provs.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load AML history");
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  /* Provider currently set on this case (falls back to firm default).
     New AML orders use this; existing checks keep their original provider. */
  const activeProvider = (caseData?.aml_provider || providers.default || "thirdfort").toLowerCase();

  const handleSwitchProvider = async (next) => {
    if (next === activeProvider) return;
    setSavingProvider(true);
    try {
      await API.put(`/cases/${caseId}/aml-provider`, { provider: next });
      if (onCaseChange) {
        const { data } = await API.get(`/cases/${caseId}`);
        onCaseChange(data);
      }
      toast.success(`Default AML provider for this case set to ${next}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change provider");
    } finally {
      setSavingProvider(false);
    }
  };

  const clients = useMemo(() => {
    return (caseData?.clients || []).map((c) => ({
      ...c,
      key: c.id || `pending:${c.email}`,
      lookup_id: c.id || `pending:${c.email}`,
    }));
  }, [caseData?.clients]);

  // Group checks by client_id. A pending client's checks live under
  // their "pending:{email}" key; saved clients use their UUID.
  const checksByClient = useMemo(() => {
    const m = {};
    for (const c of history.checks) {
      const k = c.client_id;
      (m[k] = m[k] || []).push(c);
    }
    return m;
  }, [history.checks]);

  const handleOrderNew = async (client, checkType = "aml") => {
    if (!client.email) {
      toast.error("Client has no email address.");
      return;
    }
    const label = checkType === "biometric" ? "biometric ID-V"
                : checkType === "both"      ? "AML + biometric ID-V"
                : "AML check";
    const tid = toast.loading(`Ordering ${label} for ${client.name || client.email}…`);
    try {
      await API.post(
        `/cases/${caseId}/clients/${client.lookup_id}/aml-check`,
        { send_email: true, check_type: checkType },
      );
      await load();
      if (onCaseChange) {
        const { data } = await API.get(`/cases/${caseId}`);
        onCaseChange(data);
      }
      toast.success(
        `${label} ordered — verification link emailed to ${client.email}`,
        { id: tid },
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to order check", { id: tid });
    }
  };

  const handleUpdate = (updated) => {
    setHistory((h) => ({
      ...h,
      checks: h.checks.map((c) => (c.id === updated.id ? updated : c)),
    }));
    // Refresh case mirror in the background — the check refresh
    // probably changed the verdict bucket on the client row.
    if (onCaseChange) {
      API.get(`/cases/${caseId}`).then(({ data }) => onCaseChange(data)).catch(() => {});
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500 p-6">Loading AML history…</div>;
  }

  const summary = history.case_summary || {};
  const summaryToneCls = {
    clear: "bg-emerald-50 border-emerald-200 text-emerald-800",
    warn: "bg-amber-50 border-amber-200 text-amber-800",
    fail: "bg-rose-50 border-rose-200 text-rose-800",
    open: "bg-blue-50 border-blue-200 text-blue-800",
    none: "bg-slate-50 border-slate-200 text-slate-700",
  }[summary.state] || "bg-slate-50 border-slate-200 text-slate-700";
  const SummaryIcon = ({ clear: CheckCircle, fail: Warning, warn: Warning, open: Clock, none: ShieldCheck }[summary.state]) || ShieldCheck;

  return (
    <div className="space-y-6" data-testid="aml-tab">
      {/* Case-level summary banner — kept concise. The provider
          switcher used to expose SmartSearch / Thirdfort; we retired
          that when Didit became the HMLR-compliant default. Legacy
          checks (if any) still render below via ClientChecksCard. */}
      <div className={`border rounded-lg p-4 flex items-start gap-3 ${summaryToneCls}`}>
        <SummaryIcon weight="fill" className="w-5 h-5 mt-0.5 shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>AML / ID-V status</h3>
          <p className="text-xs mt-0.5">{summary.summary || "No checks ordered."}</p>
          <p className="text-[11px] mt-1 opacity-80" data-testid="aml-active-provider">
            Preferred provider: <span className="font-semibold">Didit</span> (HMLR-compliant — NFC + Active liveness + AML + PoA)
            {history.checks.length > 0 && ` · ${history.checks.length} legacy check${history.checks.length === 1 ? "" : "s"} on file`}
          </p>
        </div>
      </div>

      {clients.length === 0 ? (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-10 text-center">
          <ShieldCheck weight="duotone" className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <p className="text-sm text-slate-600">No clients on this case yet.</p>
          <p className="text-xs text-slate-500 mt-1">Add a client (under the Clients section) before ordering AML checks.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {clients.map((client) => {
            const legacyChecks = checksByClient[client.key] || [];
            return (
              <div key={client.key} className="space-y-3">
                {/* Didit is the sole HMLR-compliant provider — the
                    "Order check" dropdown that used to expose
                    Thirdfort / SmartSearch has been retired. */}
                <DiditVerifyCard caseId={caseId} client={{
                  id: client.id || (client.email ? `email:${client.email}` : client.key),
                  first_name: client.first_name || (client.name || "").split(" ")[0],
                  last_name: client.last_name || (client.name || "").split(" ").slice(1).join(" "),
                  email: client.email,
                }} />
                {/* Legacy checks still visible for the audit trail of
                    cases created before the retirement. New checks can
                    only be ordered via Didit. */}
                {legacyChecks.length > 0 && (
                  <ClientChecksCard
                    caseId={caseId}
                    client={client}
                    checks={legacyChecks}
                    onUpdate={handleUpdate}
                    onOrderNew={() => {}}
                    readOnly
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
